/**
 * The in-cell editor overlay (Requirements 2.1–2.5, 2.9, 2.10, 3.1).
 *
 * A `<textarea>` floating over the active cell, plus a listbox of Core_Formula_Set
 * suggestions. Every decision belongs to `src/grid/editor.ts`; this module owns only
 * what needs a DOM — placement, focus, the caret, and the list.
 *
 * Three decisions:
 *
 * **A textarea, not an input.** `Alt+Enter` puts a real newline into the cell's
 * content, and only a textarea can show one. The overlay grows a row per line, so
 * what the user sees is what the cell will hold.
 *
 * **The editor interprets no keys, with one exception: its own listbox.** Key presses
 * go straight to the caller through {@link CellEditorCallbacks.onKeyDown}, which routes
 * them through the same binding table the grid uses, with `mode: 'edit'`. One table,
 * two modes — which is how `Enter` commits here and moves the selection there without
 * either being a special case. The exception is the suggestion list: `ArrowDown` and
 * `ArrowUp` move its active option and are consumed here, and `Enter` or `Tab` accepts
 * the active option *only while one is active*, which is the ARIA combobox contract.
 * With no option active — the state every edit starts in and every keystroke returns
 * to — `Enter` and `Tab` reach the binding table and commit, so Requirements 2.2 and
 * 2.3 are untouched by the list's existence.
 *
 * **The list is announced, not just drawn.** The textarea is a `combobox` owning a
 * `listbox`, and the active option travels through `aria-activedescendant` rather than
 * through focus, so the textarea keeps the caret while a screen reader reads each
 * suggestion. Requirement 2.10 says "display a list"; a canvas grid has no other way
 * for that list to reach a screen-reader user.
 *
 * **The caret lives in the DOM, and the session does not carry it.** `EditSession`
 * holds the text; `textarea.selectionStart` is the caret. Keeping one copy of the
 * caret means the hint list can never point at a position the user has already left.
 */

import type { EditorHints, EditSession } from '../grid/editor.js';
import { cycleAnchorAtCaret, editorHints, insertLineBreak, withText } from '../grid/editor.js';
import { el } from './dom.js';

/** Most suggestions shown at once. Beyond this the list stops being a shortcut. */
export const MAX_SUGGESTIONS = 8;

/** Where the overlay sits, in CSS pixels relative to the grid pane. */
export interface EditorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CellEditorCallbacks {
  /** Every key press inside the editor, for the caller's binding-table dispatch. */
  onKeyDown(event: KeyboardEvent): void;
  /** Fired whenever the text or the caret moves, so the caller can repaint. */
  onChange(session: EditSession, hints: EditorHints): void;
  /** Fired when the textarea loses focus. */
  onBlur?(): void;
}

export interface CellEditor {
  element: HTMLElement;
  textarea: HTMLTextAreaElement;
  /** The suggestion listbox (Requirement 2.10). */
  list: HTMLElement;
  /** Index of the highlighted suggestion, or `-1` when none is. */
  activeSuggestion(): number;
  /** Opens the overlay over `rect` and focuses it. */
  open(session: EditSession, rect: EditorRect): void;
  isOpen(): boolean;
  /** The session, with the textarea's current text. */
  session(): EditSession | null;
  /** Caret offset in the textarea. */
  caret(): number;
  /** Moves the overlay, after a scroll or a resize. */
  place(rect: EditorRect): void;
  /** Reads the textarea back into the session and republishes the hints. */
  sync(): EditSession | null;
  /** Inserts a line break at the caret (`Alt+Enter`, Requirement 3.1). */
  lineBreak(): void;
  /** Cycles anchoring of the reference under the caret (`F4`). False if none. */
  cycleAnchor(): boolean;
  /** Replaces the name being typed with a chosen suggestion. */
  choose(name: string): void;
  /**
   * Replaces the whole text and places the caret, leaving focus alone.
   *
   * Point mode's entry point: a click on the grid rewrites the formula while the caret
   * has to stay where it is, so unlike {@link CellEditor.choose} this does not call
   * `textarea.focus()`.
   */
  setText(text: string, caret: number): void;
  /** Reference highlights and suggestions for the current text (R2.9, R2.10). */
  hints(): EditorHints;
  /** Closes the overlay and reports the content the cell held before the edit. */
  close(): string | null;
}

const NO_HINTS: EditorHints = { formula: false, highlights: [], prefix: null, suggestions: [] };

/** DOM id of the suggestion listbox, for `aria-controls`. */
const LIST_ID = 'cell-editor-suggestions';

/** DOM id of one option, for `aria-activedescendant`. */
function optionId(index: number): string {
  return `${LIST_ID}-${index}`;
}

export function createCellEditor(callbacks: CellEditorCallbacks): CellEditor {
  const textarea = el('textarea', {
    className: 'cell-editor-input',
    testid: 'cell-editor',
    ariaLabel: 'Cell editor',
    attrs: {
      rows: '1',
      autocomplete: 'off',
      spellcheck: 'false',
      wrap: 'off',
      // The combobox half of the ARIA pattern. `aria-expanded` follows the list's
      // visibility and `aria-activedescendant` follows the highlighted option, so a
      // screen reader reads each suggestion while the caret stays in the textarea.
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': 'false',
      'aria-controls': LIST_ID,
    },
  });
  const list = el('ul', {
    className: 'cell-editor-suggestions',
    testid: 'fn-suggestions',
    role: 'listbox',
    ariaLabel: 'Function suggestions',
    attrs: { id: LIST_ID },
  });
  const element = el('div', { className: 'cell-editor', attrs: { hidden: 'hidden' } }, [
    textarea,
    list,
  ]);

  let session: EditSession | null = null;
  /** Highlighted suggestion, or `-1`. Reset whenever the list is re-rendered. */
  let active = -1;
  /** The suggestions currently listed, so `Enter` can accept one without a re-derive. */
  let listed: string[] = [];

  function caret(): number {
    return textarea.selectionStart ?? textarea.value.length;
  }

  function hints(): EditorHints {
    if (session === null) return NO_HINTS;
    return editorHints(session.text, session.addr.sheet, caret());
  }

  /** Points `aria-activedescendant` and `aria-selected` at the highlighted option. */
  function paintActive(): void {
    const options = [...list.children];
    options.forEach((option, index) => {
      option.setAttribute('aria-selected', index === active ? 'true' : 'false');
      option.classList.toggle('is-active', index === active);
    });
    if (active < 0) textarea.removeAttribute('aria-activedescendant');
    else textarea.setAttribute('aria-activedescendant', optionId(active));
  }

  function renderSuggestions(current: EditorHints): void {
    const names = current.suggestions.slice(0, MAX_SUGGESTIONS);
    // A *different* set of names is a different list, and keeping the old index would
    // highlight a suggestion the user never saw. The same set is the common case —
    // every caret move republishes the hints, and `keyup` republishes after the arrow
    // key that moved the highlight — so the highlight has to survive it.
    const unchanged = names.length === listed.length && names.every((n, i) => n === listed[i]);
    list.textContent = '';
    listed = names;
    list.hidden = listed.length === 0;
    textarea.setAttribute('aria-expanded', listed.length === 0 ? 'false' : 'true');
    active = unchanged ? Math.min(active, listed.length - 1) : -1;
    listed.forEach((name, index) => {
      const option = el('li', {
        className: 'cell-editor-suggestion',
        text: name,
        role: 'option',
        attrs: { id: optionId(index), 'aria-selected': 'false' },
      });
      option.addEventListener('mousedown', (event: MouseEvent) => {
        // `mousedown`, not `click`: a click blurs the textarea first, and the caret
        // the replacement needs would be gone by the time the handler ran.
        event.preventDefault();
        choose(name);
      });
      list.appendChild(option);
    });
    paintActive();
  }

  /**
   * Moves the highlight (`ArrowDown` / `ArrowUp`), wrapping at both ends.
   *
   * From "nothing highlighted", `ArrowDown` takes the first suggestion and `ArrowUp`
   * the last, which is what a combobox does and what makes the list reachable without
   * a pointer.
   */
  function moveActive(delta: 1 | -1): boolean {
    if (list.hidden || listed.length === 0) return false;
    const count = listed.length;
    active = active < 0 ? (delta === 1 ? 0 : count - 1) : (active + delta + count) % count;
    paintActive();
    return true;
  }

  function publish(): void {
    if (session === null) return;
    const current = hints();
    // One row per line, so a line break grows the overlay instead of hiding the text
    // the user just broke onto a new line.
    textarea.rows = Math.max(1, session.text.split('\n').length);
    renderSuggestions(current);
    callbacks.onChange(session, current);
  }

  function setText(text: string, at: number): void {
    if (session === null) return;
    session = withText(session, text);
    textarea.value = text;
    textarea.setSelectionRange(at, at);
    publish();
  }

  function sync(): EditSession | null {
    if (session === null) return null;
    session = withText(session, textarea.value);
    publish();
    return session;
  }

  function choose(name: string): void {
    if (session === null) return;
    const at = caret();
    const prefix = hints().prefix ?? '';
    const head = session.text.slice(0, at - prefix.length);
    const tail = session.text.slice(at);
    const inserted = `${name}(`;
    textarea.focus();
    setText(head + inserted + tail, head.length + inserted.length);
  }

  textarea.addEventListener('input', () => {
    sync();
  });
  textarea.addEventListener('blur', () => {
    callbacks.onBlur?.();
  });
  textarea.addEventListener('keyup', () => {
    // Arrow keys move the caret without an `input` event, and the suggestion list
    // follows the caret rather than the last edit.
    sync();
  });
  textarea.addEventListener('keydown', (event: KeyboardEvent) => {
    // The listbox's own keys, and only while it is open. Everything else — including
    // `Enter` and `Tab` with nothing highlighted — goes to the binding table.
    if (!event.ctrlKey && !event.metaKey && !event.altKey && !list.hidden) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (moveActive(event.key === 'ArrowDown' ? 1 : -1)) {
          event.preventDefault();
          return;
        }
      } else {
        const prefixLength = (hints().prefix ?? '').length;
        if (
          (event.key === 'Tab' && listed.length > 0 && prefixLength > 0) ||
          (active >= 0 && (event.key === 'Enter' || event.key === 'Tab'))
        ) {
          const targetIdx = active >= 0 ? active : 0;
          const name = listed[targetIdx];
          if (name !== undefined) {
            event.preventDefault();
            choose(name);
            return;
          }
        }
      }
    }
    callbacks.onKeyDown(event);
  });

  function place(rect: EditorRect): void {
    element.style.left = `${rect.x}px`;
    element.style.top = `${rect.y}px`;
    element.style.width = `${rect.w}px`;
    element.style.height = `${rect.h}px`;
  }

  return {
    element,
    textarea,
    list,

    activeSuggestion: () => active,

    open(next: EditSession, rect: EditorRect): void {
      session = next;
      element.hidden = false;
      place(rect);
      textarea.value = next.text;
      textarea.rows = Math.max(1, next.text.split('\n').length);
      textarea.focus();
      textarea.setSelectionRange(next.text.length, next.text.length);
      publish();
    },

    isOpen(): boolean {
      return session !== null;
    },

    session(): EditSession | null {
      return session;
    },

    caret,
    place,
    sync,

    lineBreak(): void {
      if (session === null) return;
      const start = textarea.selectionStart ?? session.text.length;
      const end = textarea.selectionEnd ?? start;
      const broken = insertLineBreak(session.text, start, end);
      setText(broken.text, broken.caret);
    },

    cycleAnchor(): boolean {
      if (session === null) return false;
      const cycled = cycleAnchorAtCaret(session.text, caret());
      if (cycled === null) return false;
      setText(cycled.text, cycled.caret);
      return true;
    },

    choose,
    setText,
    hints,

    close(): string | null {
      const prior = session?.prior ?? null;
      session = null;
      element.hidden = true;
      list.hidden = true;
      list.textContent = '';
      listed = [];
      active = -1;
      textarea.setAttribute('aria-expanded', 'false');
      textarea.removeAttribute('aria-activedescendant');
      textarea.value = '';
      return prior;
    },
  };
}
